import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import {
  getStoredAccessToken,
  getStoredMeetingSessionId,
  getStoredMeetingUserId,
  hydrateMeetingIdentity,
} from '../../utils/meetingAuth';

const toId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value._id || value.id || value);
};

const Rejoin = () => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const [status, setStatus] = useState('Checking your session...');
  const [error, setError] = useState('');
  const authUser = useSelector((state) => state.auth.user);

  useEffect(() => {
    let cancelled = false;
    let redirectTimer = null;

    const redirect = (target, delay = 1500) => {
      redirectTimer = window.setTimeout(() => {
        if (!cancelled) navigate(target, { replace: true });
      }, delay);
    };

    const attemptRejoin = async () => {
      const cleanRoomId = String(roomId || '').trim();
      hydrateMeetingIdentity();

      if (!cleanRoomId) {
        setError('Invalid meeting ID');
        toast.error('Invalid meeting ID');
        redirect('/', 2000);
        return;
      }

      const token = getStoredAccessToken();
      const userId = getStoredMeetingUserId() || toId(authUser);
      const sessionId = getStoredMeetingSessionId();

      if (!token || !userId || !sessionId) {
        setStatus('Please login to rejoin');
        toast.warning('Please login to rejoin the meeting');
        redirect(
          `/login?redirect=${encodeURIComponent(`/join-room?roomIdGet=${cleanRoomId}`)}`,
          2000
        );
        return;
      }

      try {
        setStatus('Verifying meeting status...');

        const { data: room } = await api.get(`/rooms/${cleanRoomId}`);

        if (
          !room ||
          room.isActive === false ||
          room.meetingStatus === 'ended' ||
          room.meetingSatatus === 'ended'
        ) {
          throw new Error('This meeting has ended or is no longer active');
        }

        const participant = (room.participants || []).find(
          (item) =>
            toId(item.user) === String(userId) &&
            String(item.sessionId || '') === String(sessionId)
        );

        if (!participant) {
          // Re-enter through the safe REST join flow instead of self-admitting.
          setStatus('Restoring your waiting-room session...');
          navigate(
            `/join-room?roomIdGet=${encodeURIComponent(cleanRoomId)}`,
            { replace: true }
          );
          return;
        }

        if (participant.status === 'rejected' && room.meetingType !== 'interview') {
          throw new Error('Your classroom join request was rejected');
        }

        sessionStorage.removeItem('rejoin-room');
        sessionStorage.removeItem('meetingEnd');
        sessionStorage.setItem('rejoinStatus', 'true');
        sessionStorage.setItem('lastRoomId', cleanRoomId);
        sessionStorage.setItem('statusget', participant.status || 'waiting');

        setStatus(
          participant.status === 'waiting'
            ? 'Reconnecting to the waiting room...'
            : 'Reconnecting to the meeting...'
        );

        redirect(
          `/join-room?roomIdGet=${encodeURIComponent(cleanRoomId)}&rejoin=true`,
          500
        );
      } catch (requestError) {
        if (cancelled) return;

        const message =
          requestError.response?.data?.message ||
          requestError.message ||
          'Failed to rejoin meeting';

        console.error('Rejoin error:', requestError);
        setError(message);
        toast.error(message);
        redirect('/', 2500);
      }
    };

    attemptRejoin();

    return () => {
      cancelled = true;
      if (redirectTimer) window.clearTimeout(redirectTimer);
    };
  }, [authUser, navigate, roomId]);

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-900 p-4 text-white">
        <div className="mb-4 text-5xl text-red-500">⚠️</div>
        <h2 className="mb-2 text-center text-xl font-semibold text-red-400">
          {error}
        </h2>
        <p className="text-sm text-gray-400">Redirecting to the home page...</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center bg-gray-900 p-4 text-white">
      <div className="mb-6 h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-indigo-500" />
      <h2 className="mb-2 animate-pulse text-center text-2xl font-semibold">
        {status}
      </h2>
      <p className="text-sm text-gray-400">Room ID: {roomId}</p>
      <p className="mt-4 text-xs text-gray-500">
        Reconnecting through the secure meeting session
      </p>
    </div>
  );
};

export default Rejoin;
